/** Extract markdown link [name](url) from text. Returns null if no link found. */
export function extractMarkdownLink(text: string): { name: string; url: string } | null {
  const match = text.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  return match ? { name: match[1]!, url: match[2]! } : null;
}

/** Check if a trimmed string is numeric (allows commas, spaces, units suffix). */
export function isNumeric(text: string): boolean {
  return /^[\d,.\s]+(\s+\S+)?$/.test(text.trim());
}

/** Known phase/status names mapped to semantic color categories. */
const PHASE_COLORS: Record<string, string> = {
  planbesked: "phase-early",
  planuppdrag: "phase-early",
  "samråd": "phase-mid",
  samrad: "phase-mid",
  granskning: "phase-mid",
  antagande: "phase-late",
  laga_kraft: "phase-complete",
  "laga kraft": "phase-complete",
  open: "phase-early",
  announced: "phase-early",
  planned: "phase-early",
  closed: "phase-mid",
  decided: "phase-complete",
  approved: "phase-complete",
  potential: "phase-mid",
  early_stage: "phase-early",
  in_operation: "phase-complete",
};

/** Get the CSS class suffix for a known phase/status name. Returns null if unknown. */
export function getPhaseClass(phase: string): string | null {
  return PHASE_COLORS[phase.toLowerCase().trim()] ?? null;
}

/** Parse stat block: "value | label" per line. */
export function parseStatLines(content: string): { value: string; label: string }[] {
  return content
    .trim()
    .split("\n")
    .map((line) => {
      const sep = line.indexOf("|");
      if (sep === -1) return null;
      return { value: line.slice(0, sep).trim(), label: line.slice(sep + 1).trim() };
    })
    .filter((x): x is { value: string; label: string } => x !== null);
}

/** Parse bar block: "label: value" per line. */
export function parseBarLines(content: string): { label: string; value: number }[] {
  return content
    .trim()
    .split("\n")
    .map((line) => {
      const sep = line.indexOf(":");
      if (sep === -1) return null;
      const val = parseFloat(line.slice(sep + 1).replace(/,/g, "").trim());
      if (isNaN(val)) return null;
      return { label: line.slice(0, sep).trim(), value: val };
    })
    .filter((x): x is { label: string; value: number } => x !== null);
}

/** Parse callout block: first line = label, rest = body. */
export function parseCallout(content: string): { label: string; body: string } {
  const lines = content.trim().split("\n");
  return { label: lines[0] ?? "", body: lines.slice(1).join("\n").trim() };
}

/** Parse entity list: pipe-separated columns per line. */
export function parseEntityLines(content: string): string[][] {
  return content
    .trim()
    .split("\n")
    .map((line) => line.split("|").map((col) => col.trim()))
    .filter((cols) => cols.length > 0 && cols[0] !== "");
}

/** Parse markdown table: first row = headers, rest = data rows. Strips separator row (---). */
export function parseTableLines(content: string): { headers: string[]; rows: string[][] } {
  const lines = content
    .trim()
    .split("\n")
    .map((line) => line.replace(/^\||\|$/g, "").trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = lines[0]!.split("|").map((h) => h.trim());

  const dataLines = lines.slice(1).filter((line) => !/^[-|\s:]+$/.test(line));
  const rows = dataLines.map((line) => line.split("|").map((c) => c.trim()));

  return { headers, rows };
}

/** Timeline phase entry. */
export interface TimelinePhase {
  name: string;
  date: string;
  isCurrent: boolean;
  isForecasted: boolean;
}

/** Timeline entity block. */
export interface TimelineBlock {
  name: string;
  link: { name: string; url: string } | null;
  phases: TimelinePhase[];
}

/** Parse timeline block: alternating name + phases lines. */
export function parseTimelineBlocks(content: string): TimelineBlock[] {
  const lines = content.trim().split("\n").filter((l) => l.trim().length > 0);
  const blocks: TimelineBlock[] = [];

  for (let i = 0; i < lines.length; i += 2) {
    const nameLine = lines[i]!.trim();
    const phaseLine = lines[i + 1]?.trim();
    if (!phaseLine) continue;

    const link = extractMarkdownLink(nameLine);
    const name = link ? link.name : nameLine;

    const phases = phaseLine.split("|").map((segment) => {
      const sep = segment.indexOf(":");
      if (sep === -1) return null;
      let phaseName = segment.slice(0, sep).trim();
      const isCurrent = phaseName.endsWith("*");
      if (isCurrent) phaseName = phaseName.slice(0, -1);

      let date = segment.slice(sep + 1).trim();
      const isForecasted = date.startsWith("~");
      if (isForecasted) date = date.slice(1).trim();

      return { name: phaseName, date, isCurrent, isForecasted };
    }).filter((x): x is TimelinePhase => x !== null);

    blocks.push({ name, link, phases });
  }

  return blocks;
}

/** Scatter plot point. */
export interface ScatterPoint {
  link: { name: string; url: string } | null;
  label: string;
  x: string;
  y: string;
  size: number;
}

/** Scatter plot data with axis declarations. */
export interface ScatterData {
  axes: { x: string; y: string; size: string };
  points: ScatterPoint[];
}

/** Parse scatter block: first line = axis declarations, rest = data points. */
export function parseScatterData(content: string): ScatterData | null {
  const lines = content.trim().split("\n").filter((l) => l.trim().length > 0);
  if (lines.length < 2) return null;

  // Parse axis line: "x: readiness | y: probability | size: units"
  const axisLine = lines[0]!;
  const axisParts = axisLine.split("|").map((p) => p.trim());
  const axes = { x: "", y: "", size: "" };
  for (const part of axisParts) {
    const [key, val] = part.split(":").map((s) => s.trim());
    if (key === "x") axes.x = val ?? "";
    else if (key === "y") axes.y = val ?? "";
    else if (key === "size") axes.size = val ?? "";
  }

  if (!axes.x || !axes.y) return null;

  // Parse data lines: "[Entity](url) | x_value | y_value | size_value"
  const points: ScatterPoint[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i]!.split("|").map((c) => c.trim());
    if (cols.length < 4) continue;

    const link = extractMarkdownLink(cols[0]!);
    const label = link ? link.name : cols[0]!;

    points.push({
      link,
      label,
      x: cols[1]!,
      y: cols[2]!,
      size: parseFloat(cols[3]!.replace(/,/g, "")) || 0,
    });
  }

  return points.length > 0 ? { axes, points } : null;
}

/** Property card section types. */
export interface PropertyCardData {
  header: {
    name: string;
    link: { name: string; url: string } | null;
    type: string;
    status: string;
    badge: string | null;
  };
  metrics: { value: string; label: string }[];
  ownership: { name: string; share: number }[];
  plans: { link: { name: string; url: string } | null; label: string; phase: string; meta: string }[];
  permits: { link: { name: string; url: string } | null; label: string; status: string; meta: string }[];
  zoning: string[];
}

/** Parse a key: value pair from a pipe-separated metadata line. */
function parseMetaField(parts: string[], key: string): string {
  for (const part of parts) {
    const [k, v] = part.split(":").map((s) => s.trim());
    if (k?.toLowerCase() === key) return v ?? "";
  }
  return "";
}

/** Parse property card block: sections delimited by # headers. */
export function parsePropertyCard(content: string): PropertyCardData | null {
  const sections: Record<string, string[]> = {};
  let currentSection = "header";
  sections[currentSection] = [];

  for (const line of content.trim().split("\n")) {
    const trimmed = line.trim();
    if (trimmed.startsWith("# ")) {
      currentSection = trimmed.slice(2).trim().toLowerCase();
      sections[currentSection] = [];
    } else if (trimmed.length > 0) {
      (sections[currentSection] ??= []).push(trimmed);
    }
  }

  // Header: first line is name (possibly a link), second is metadata
  const headerLines = sections["header"] ?? [];
  if (headerLines.length === 0) return null;

  const nameLink = extractMarkdownLink(headerLines[0]!);
  const name = nameLink ? nameLink.name : headerLines[0]!;
  const metaParts = (headerLines[1] ?? "").split("|").map((p) => p.trim());

  const header = {
    name,
    link: nameLink,
    type: parseMetaField(metaParts, "type"),
    status: parseMetaField(metaParts, "status"),
    badge: parseMetaField(metaParts, "badge") || null,
  };

  // Metrics: "value | label" per line
  const metrics = (sections["metrics"] ?? [])
    .map((line) => {
      const sep = line.indexOf("|");
      if (sep === -1) return null;
      return { value: line.slice(0, sep).trim(), label: line.slice(sep + 1).trim() };
    })
    .filter((x): x is { value: string; label: string } => x !== null);

  // Ownership: "name | share%" per line
  const ownership = (sections["ownership"] ?? [])
    .map((line) => {
      const sep = line.indexOf("|");
      if (sep === -1) return null;
      const share = parseFloat(line.slice(sep + 1).replace(/%/g, "").trim());
      if (isNaN(share)) return null;
      return { name: line.slice(0, sep).trim(), share };
    })
    .filter((x): x is { name: string; share: number } => x !== null);

  // Plans: "[entity](url) | phase | meta" per line
  const plans = (sections["plans"] ?? [])
    .map((line) => {
      const cols = line.split("|").map((c) => c.trim());
      if (cols.length < 2) return null;
      const link = extractMarkdownLink(cols[0]!);
      return {
        link,
        label: link ? link.name : cols[0]!,
        phase: cols[1] ?? "",
        meta: cols.slice(2).join(" · ").trim(),
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  // Permits: same format as plans
  const permits = (sections["permits"] ?? [])
    .map((line) => {
      const cols = line.split("|").map((c) => c.trim());
      if (cols.length < 2) return null;
      const link = extractMarkdownLink(cols[0]!);
      return {
        link,
        label: link ? link.name : cols[0]!,
        status: cols[1] ?? "",
        meta: cols.slice(2).join(" · ").trim(),
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  // Zoning: comma-separated on one line
  const zoningLine = (sections["zoning"] ?? []).join(", ");
  const zoning = zoningLine ? zoningLine.split(",").map((z) => z.trim()).filter((z) => z.length > 0) : [];

  return { header, metrics, ownership, plans, permits, zoning };
}

/** Breakdown group: header + segments. */
export interface BreakdownSegment {
  label: string;
  value: number;
}

export interface BreakdownGroup {
  header: string;
  segments: BreakdownSegment[];
}

/** Parse breakdown block: groups separated by blank lines.
 *  Each group: first line is "label — total", rest are "name: value" segments.
 *  Segments can be on one line separated by " | " or one per line.
 */
export function parseBreakdownGroups(content: string): BreakdownGroup[] {
  const rawGroups = content.trim().split(/\n\s*\n/);
  const groups: BreakdownGroup[] = [];

  for (const raw of rawGroups) {
    const lines = raw.trim().split("\n").filter((l) => l.trim().length > 0);
    if (lines.length < 2) continue;

    const header = lines[0]!.trim();

    // Remaining lines may be one-per-line or pipe-separated on a single line
    const segments: BreakdownSegment[] = [];
    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i]!.split("|").map((p) => p.trim());
      for (const part of parts) {
        const sep = part.lastIndexOf(":");
        if (sep === -1) continue;
        const label = part.slice(0, sep).trim();
        const val = parseFloat(part.slice(sep + 1).replace(/[,\s]/g, "").trim());
        if (label && !isNaN(val)) {
          segments.push({ label, value: val });
        }
      }
    }

    if (segments.length > 0) {
      groups.push({ header, segments });
    }
  }

  return groups;
}

/** The rich block language tags. */
export const RICH_BLOCK_LANGUAGES = new Set([
  "stat", "bars", "entities",
  "timeline", "callout", "compare",
  "kpi", "gauge",
  "scatter", "property",
  "breakdown",
]);
